/* eslint-disable no-restricted-imports */
import React, {
    createContext,
    ReactNode,
    useContext,
    useState,
    useEffect,
    useCallback,
} from 'react'
import {ReplayInitiationData} from '@colonist/game-base-types'
import {getReplayUrlActionIndex} from '../../../../code/client/shared/scripts/ui_replay/ReplayUrlUtilities'
import {filterGameLogsForPerspectives} from '@colonist/game-state-types'
import {PlayerColor} from '@colonist/game-states'

// TODO remove once UIReplayController is converted to ReactJS
let globalFocusLogIndexSetter: undefined | ((logIndex: number | null) => void)
export function setGlobalFocusLogIndex(logIndex: number | null) {
    if(globalFocusLogIndexSetter) globalFocusLogIndexSetter(logIndex)
}

interface UIGameReplayContextProps {
    replayActionIndex: number
    focusLogIndex: number | null
    onClickLog: (gameLogIndex: number) => void
    replayData?: ReplayInitiationData | undefined
    setReplayActionIndex: (replayActionIndex: number) => void
    replayInitialActionIndex: number
    currentPlayerPerspective?: PlayerColor | undefined
    setCurrentPlayerPerspective: (color: PlayerColor) => void
    actionIndexForEndgame: number
    showEndGameScreen: boolean
    isReplay: boolean
    setIsReplay: (isReplay: boolean) => void
}

export const UIGameReplayContext = createContext<UIGameReplayContextProps | undefined>(undefined)

export const useUIGameReplayContext = () => {
    const context = useContext(UIGameReplayContext)
    if(!context) {
        throw new Error('useUIGameReplayContext must be used within a UIGameReplayProvider')
    }
    return context
}
interface UIGameReplayProviderProps {
    children: ReactNode
    initialReplayData?: ReplayInitiationData
    initialPlayerPerspective?: PlayerColor
    showEndGameScreen?: boolean
    setShowEndGameScreen?: (show: boolean) => void
    defaultIsReplay: boolean
}

export function UIGameReplayProvider({
    initialReplayData,
    children,
    initialPlayerPerspective,
    showEndGameScreen = false,
    setShowEndGameScreen,
    defaultIsReplay,
}: UIGameReplayProviderProps) {
    const replayInitialActionIndex = getReplayUrlActionIndex(window.location.href) || -1
    const [currentPlayerPerspective, setCurrentPlayerPerspective] = useState(initialPlayerPerspective)
    const [replayActionIndex, setReplayActionIndex] = useState<number>(replayInitialActionIndex)
    const actionIndexForEndgame = initialReplayData ? initialReplayData.eventHistory.events.length - 1 : -1
    const [isReplay, setIsReplay] = useState(defaultIsReplay)
    const [focusLogIndex, setFocusLogIndex] = useState<number | null>(isReplay ? replayInitialActionIndex : null)

    globalFocusLogIndexSetter = setFocusLogIndex
    useEffect(() => {
        if(replayActionIndex === actionIndexForEndgame) {
            setShowEndGameScreen?.(true)
        } else {
            setShowEndGameScreen?.(false)
        }
    }, [replayActionIndex, actionIndexForEndgame])

    const handleUpdateFocusLogIndex = useCallback(() => {
        if(!initialReplayData || (currentPlayerPerspective == null)) {
            return
        }
        if(replayActionIndex == replayInitialActionIndex) {
            setFocusLogIndex(0)
            return
        }
        const gameLogState = structuredClone(initialReplayData.eventHistory.events[replayActionIndex].stateChange.gameLogState)
        if(!gameLogState) {
            return
        }
        filterGameLogsForPerspectives(gameLogState, [currentPlayerPerspective])
        const actionIndices = Object.keys(gameLogState)

        setFocusLogIndex(Number(actionIndices[actionIndices.length - 1]))
    }, [initialReplayData, replayActionIndex, replayInitialActionIndex, currentPlayerPerspective])

    useEffect(() => {
        handleUpdateFocusLogIndex()
    }, [handleUpdateFocusLogIndex])

    const onClickLog = (gameLogIndex: number) => {
        if(gameLogIndex == 0) {
            setReplayActionIndex(0)
            return
        }
        if(!initialReplayData) {
            return
        }
        for(let actionIndex = 0; actionIndex < initialReplayData.eventHistory.events.length; actionIndex++) {
            const gameLogState = initialReplayData.eventHistory.events[actionIndex].stateChange.gameLogState
            for(const key in gameLogState) {
                const index = Number(key)
                if(index == gameLogIndex) {
                    setReplayActionIndex(actionIndex)
                    return
                }
            }
        }
    }

    return (
        <UIGameReplayContext.Provider
            value={{
                isReplay: isReplay,
                setIsReplay: setIsReplay,
                replayData: initialReplayData,
                replayActionIndex: replayActionIndex,
                focusLogIndex: focusLogIndex,
                setReplayActionIndex: setReplayActionIndex,
                onClickLog: onClickLog,
                replayInitialActionIndex: replayInitialActionIndex,
                currentPlayerPerspective: currentPlayerPerspective,
                setCurrentPlayerPerspective: setCurrentPlayerPerspective,
                actionIndexForEndgame: actionIndexForEndgame,
                showEndGameScreen: showEndGameScreen,
            }}
        >
            {children}
        </UIGameReplayContext.Provider>
    )
}
